/**
 * Shared Sentry beforeSend scrubber (#186, #229) — the ONE place deciding
 * what may leave for Sentry (no BAA, so: nothing identifying).
 *
 * Two layers, because PHI arrives by two routes:
 *  - structured fields (`extra`, `contexts`) — scrubbed by KEY via
 *    scrubMedicalData (names, DOBs, member ids, benefit payloads);
 *  - free text (`message`, exception values) — an exception built from a bot
 *    or payer error can carry the identifiers in its MESSAGE, where no key
 *    exists to scrub by; those go through the pattern-based sanitizeErrorText.
 *
 * Breadcrumb `data` gets BOTH, on top of each other (#229): Sentry's console
 * integration duplicates a logged error into `data.arguments` (raw call args,
 * free text) alongside the already-scrubbed `message`, and fetch/xhr
 * breadcrumbs carry a `data.url` that's free text too — neither has a key to
 * scrub by, and either can nest inside arrays/objects. So after the key-based
 * scrubMedicalData pass redacts known PHI fields wholesale, deepPatternScrub
 * walks whatever's left — every remaining string, at any depth, in any
 * object or array — through the same pattern scrub as `message`. Redacted
 * placeholders ("[REDACTED]") pass back through harmlessly (no digit/date/
 * email pattern to mask).
 *
 * Used by both beforeSend hooks (client `src/main.tsx` and functions
 * `utils/sentry.ts`) so the client and server can't drift apart.
 */
import {scrubMedicalData} from "./scrubMedicalData.js";
import {sanitizeErrorText} from "./sanitizeErrorText.js";

/** Recursively pattern-scrub every string leaf in a value — arrays, nested
 *  objects, any depth. Used where free text can hide with no key to scrub by
 *  (breadcrumb `data`, e.g. console-log arguments or a fetch URL). */
function deepPatternScrub(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(deepPatternScrub);
  }
  if (value && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      result[key] = deepPatternScrub(v);
    }
    return result;
  }
  if (typeof value === "string") {
    return sanitizeErrorText(value);
  }
  // Mirrors sanitizeErrorText's bare-digit-run rule: a member id, SSN, or
  // epoch-timestamp DOB can arrive as a number, not just a string (e.g.
  // console.log("member", 987654321)). 5+ digits keeps short codes (HTTP
  // statuses, AAA codes) untouched.
  if (typeof value === "number" && Number.isFinite(value) &&
      /\d{5,}/.test(String(Math.trunc(Math.abs(value))))) {
    return "[id]";
  }
  return value;
}

/** The slice of a Sentry event this scrubber touches — structurally satisfied
 *  by both @sentry/react and @sentry/google-cloud-serverless event types. */
export interface ScrubbableSentryEvent {
  message?: unknown;
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  exception?: {values?: Array<{value?: string}>};
  breadcrumbs?: Array<{message?: unknown; data?: unknown}>;
}

/** Scrub a Sentry event in place (beforeSend convention) and return it. */
export function scrubSentryEvent<T extends ScrubbableSentryEvent>(event: T): T {
  if (event.extra) {
    event.extra = scrubMedicalData(event.extra);
  }
  if (event.contexts) {
    for (const [key, ctx] of Object.entries(event.contexts)) {
      if (ctx && typeof ctx === "object") {
        event.contexts[key] = scrubMedicalData(ctx as Record<string, unknown>);
      }
    }
  }
  if (typeof event.message === "string") {
    event.message = sanitizeErrorText(event.message);
  }
  for (const v of event.exception?.values ?? []) {
    if (typeof v.value === "string") {
      v.value = sanitizeErrorText(v.value);
    }
  }
  // Breadcrumbs are a side door: @sentry/react auto-captures console logs and
  // fetch/xhr trails, so a logged bot error or a URL carrying a member id lands
  // here with no key to scrub by. Same two layers — free text by pattern,
  // structured data by key.
  for (const b of event.breadcrumbs ?? []) {
    if (typeof b.message === "string") {
      b.message = sanitizeErrorText(b.message);
    }
    if (b.data && typeof b.data === "object") {
      b.data = deepPatternScrub(scrubMedicalData(b.data as Record<string, unknown>));
    }
  }
  return event;
}
